const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '19 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const churchQuestionPerspectives = [
    {
        slug: 'are-miraculous-spiritual-gifts-for-today',
        category: 'Holy Spirit and Church',
        question: 'Are miraculous spiritual gifts for today?',
        shortTitle: 'Are miraculous gifts for today?',
        summary: 'Christians agree that the Holy Spirit equips the church and that every claimed miracle or prophecy must be tested. They disagree over whether revelatory and sign gifts ceased with the apostolic foundation, may still occur but should not be expected as ordinary ministry, continue under careful biblical regulation, or should be actively sought as a normal dimension of Pentecostal mission and worship.',
        introduction: 'The debate concerns gifts such as prophecy, tongues, healing, and miracles rather than the Spirit’s continuing work in general. It depends on how the uniqueness of the apostles relates to later church ministry, whether New Testament prophecy carries the same authority as Scripture, and how experience should be welcomed, tested, and corrected.',
        seoDescription: 'Compare cessationist, open-but-cautious, continuationist, and Pentecostal views of prophecy, tongues, healing, and miracles today.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Acts 2:14–21', href: '/read/Acts.2/#Acts.2.14-Acts.2.21' },
            { label: '1 Corinthians 12:1–31', href: '/read/1Cor.12/#1Cor.12.1-1Cor.12.31' },
            { label: '1 Corinthians 13:8–13', href: '/read/1Cor.13/#1Cor.13.8-1Cor.13.13' },
            { label: '1 Corinthians 14:1–40', href: '/read/1Cor.14/#1Cor.14.1-1Cor.14.40' },
        ],
        readings: [
            {
                id: 'cessationist-apostolic-signs',
                title: 'Some spiritual gifts belonged to the apostles',
                thesis: 'Prophecy, tongues, and miraculous sign gifts authenticated the apostles and ceased as regular gifts once the church’s revelatory foundation was complete.',
                accent: 'navy',
                badges: ['Cessationist', 'Reformed', 'Conservative'],
                summary: 'Cessationists distinguish God’s freedom to perform miracles from the continuation of particular gift-bearing offices or capacities. Apostles and prophets laid a unique foundation and delivered authoritative revelation preserved in Scripture. Prayer for healing remains appropriate, but contemporary impressions or remarkable providences should not be treated as New Testament prophecy or guaranteed gifts of healing.',
                contribution: 'It strongly protects the final authority and sufficiency of Scripture, recognises the foundational role of the apostles, and offers clear safeguards against manipulative claims of revelation and healing.',
                caution: 'This view must identify a convincing biblical endpoint for gifts that the New Testament appears to distribute through the wider church and avoid allowing real abuses to determine exegesis.',
                supporters: [{ name: 'Richard B. Gaffin Jr.', sourceId: 'zondervan-gifts' }],
                predicates: [
                    {
                        label: 'The apostolic foundation is unique and non-repeatable',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'retain-commanded-form',
                        linked: true,
                    },
                    {
                        label: 'Holiness is recognised chiefly by durable fruit and obedience',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'fruit-and-obedience',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'regulated-continuationism', linked: true },
                    { type: 'contrasts with', target: 'pentecostal-expectation', linked: true },
                ],
            },
            {
                id: 'open-but-cautious',
                title: 'Spiritual gifts may continue without being required',
                thesis: 'Scripture does not prove that miraculous gifts ceased, but churches should remain cautious, test claims rigorously, and avoid centring ministry on them.',
                accent: 'amber',
                badges: ['Open but cautious', 'Evangelical', 'Moderate'],
                summary: 'This position leaves room for God to give prophecy, healing, tongues, or miracles today while emphasising that such experiences are uneven, fallible in interpretation, and never equal to Scripture. It is often shaped by both the lack of a clear cessation text and serious concern about excess, disappointment, and spiritual pressure.',
                contribution: 'It resists ruling out gifts that Scripture does not explicitly terminate while preserving pastoral restraint and freedom for Christians whose experience differs.',
                caution: 'The approach can become practically cessationist without a clear theological reason, or so noncommittal that churches receive little guidance about how gifts should be desired and tested.',
                supporters: [{ name: 'Robert L. Saucy', sourceId: 'zondervan-gifts' }],
                predicates: [
                    {
                        label: 'Spirit-empowered experience is genuine but must be evaluated by enduring fruit',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'spirit-empowered-life',
                        linked: true,
                    },
                    {
                        label: 'Apostolic instructions may preserve their purpose without identical contemporary form',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'preserve-purpose-adapt-form',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'cessationist-apostolic-signs', linked: true },
                    { type: 'qualifies', target: 'regulated-continuationism', linked: true },
                ],
            },
            {
                id: 'regulated-continuationism',
                title: 'Spiritual gifts continue under biblical guidance',
                thesis: 'The Spirit continues to distribute all New Testament gifts, while contemporary prophecy is subordinate to Scripture and must be weighed by the church.',
                accent: 'teal',
                badges: ['Continuationist', 'Charismatic', 'Evangelical'],
                summary: 'Continuationists see no biblical statement that gifts cease before Christ’s return. They commonly distinguish canonical revelation from congregational prophecy, which may contain a genuine prompting expressed through fallible human understanding. Gifts should be desired for edification, exercised in order, and tested rather than either accepted uncritically or forbidden.',
                contribution: 'It gives direct force to Paul’s commands to desire gifts and not despise prophecy while building discernment, accountability, love, and congregational edification into their practice.',
                caution: 'This view must explain how fallible prophecy can still be called prophecy, protect vulnerable people from authoritative-sounding error, and establish reliable practices for testing claims.',
                supporters: [{ name: 'Sam Storms', sourceId: 'zondervan-gifts' }],
                predicates: [
                    {
                        label: 'Spirit-empowered gifts contribute to recognisable holy life and communal edification',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'spirit-empowered-life',
                        linked: true,
                    },
                    {
                        label: 'Ministry authority follows tested calling and gifting rather than social status alone',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'gifted-for-all-pastoral-offices',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'cessationist-apostolic-signs', linked: true },
                    { type: 'overlaps with', target: 'pentecostal-expectation', linked: true },
                ],
            },
            {
                id: 'pentecostal-expectation',
                title: 'Spiritual gifts remain expected in church life',
                thesis: 'The Spirit continues to empower ordinary believers with prophecy, tongues, healing, and miracles, and churches should actively make room for these gifts under scriptural discernment.',
                accent: 'red',
                badges: ['Pentecostal', 'Charismatic', 'Continuationist', 'Missional'],
                summary: 'Classical Pentecostal teaching treats Acts and 1 Corinthians as an ongoing pattern of Spirit-empowered witness. Prophecy strengthens, encourages, and comforts the church; it is judged by Scripture and the gathered body and does not add to the biblical canon. Gifts are not marks of superior status, and their exercise requires pastoral accountability, order, and the fruit of the Spirit.',
                contribution: 'It integrates doctrine with expectant prayer, congregational participation, and mission, and it recognises the broad distribution of gifts across women and men rather than limiting ministry to professional leaders.',
                caution: 'This view must confront failed predictions, coercive healing claims, and abuses of spiritual authority with transparent correction rather than treating criticism as resistance to the Spirit.',
                supporters: [{ name: 'Assemblies of God (USA)', sourceId: 'ag-prophecy' }],
                predicates: [
                    {
                        label: 'Spirit empowerment is a visible dimension of holy and missional life',
                        topicSlug: 'how-should-holiness-be-recognised',
                        readingId: 'spirit-empowered-life',
                        linked: true,
                    },
                    {
                        label: 'Women and men receive gifts that may include authoritative public ministry',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'patterns-of-authoritative-ministry',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'regulated-continuationism', linked: true },
                    { type: 'contrasts with', target: 'cessationist-apostolic-signs', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'zondervan-gifts',
                'Richard B. Gaffin Jr., Robert L. Saucy, Sam Storms, and Douglas A. Oss',
                'Are Miraculous Gifts for Today? Four Views',
                'A Zondervan Academic comparison of cessationist, open-but-cautious, continuationist, and Pentecostal/charismatic positions.',
                'https://zondervanacademic.com/products/are-miraculous-gifts-for-today',
            ),
            source(
                'ag-prophecy',
                'Assemblies of God (USA)',
                'Modern-Day Prophecy',
                'An official Pentecostal statement affirming contemporary prophecy while requiring scriptural testing and communal accountability.',
                'https://ag.org/Beliefs/Position-Papers/Modern-Day-Prophecy',
            ),
        ],
        generation,
    },
    {
        slug: 'should-christians-tithe',
        category: 'Christian Practice',
        question: 'Should Christians tithe?',
        shortTitle: 'Should Christians tithe?',
        summary: 'Christians agree that disciples should give generously, support gospel ministry, and care for people in need. They disagree over whether the Old Testament tithe remains a binding ten-per-cent requirement, continues as a wise covenantal principle rather than detailed law, or gives way in the new covenant to voluntary, proportional, sacrificial giving without a fixed percentage.',
        introduction: 'The biblical tithe supported Levites, worship, festivals, and the poor within Israel’s covenant life. The New Testament affirms generous and planned giving but does not directly command churches to collect a universal ten per cent. The debate therefore turns on continuity between the covenants and on whether a numerical floor protects generosity or can become a substitute for whole-life stewardship.',
        seoDescription: 'Compare Christian views of tithing: a binding covenantal tithe, a ten-per-cent principle, and generous new-covenant giving without a fixed rate.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Malachi 3:6–12', href: '/read/Mal.3/#Mal.3.6-Mal.3.12' },
            { label: 'Matthew 23:23–24', href: '/read/Matt.23/#Matt.23.23-Matt.23.24' },
            { label: '1 Corinthians 9:1–18', href: '/read/1Cor.9/#1Cor.9.1-1Cor.9.18' },
            { label: '2 Corinthians 8:1–15', href: '/read/2Cor.8/#2Cor.8.1-2Cor.8.15' },
            { label: '2 Corinthians 9:6–15', href: '/read/2Cor.9/#2Cor.9.6-2Cor.9.15' },
        ],
        readings: [
            {
                id: 'covenantal-tithe',
                title: 'Christians should continue to tithe',
                thesis: 'Christians remain obligated to give a tenth because the tithe precedes Sinai and continues as God’s established provision for worship and ministry.',
                accent: 'navy',
                badges: ['Covenantal', 'Conservative', 'Tithing'],
                summary: 'This position traces tithing through Abraham, Jacob, the Mosaic law, the prophets, and Jesus’ affirmation of tithing. It treats the tenth as a continuing minimum owed to God, normally directed through the church, while offerings begin beyond that baseline. Grace deepens generosity rather than abolishing a longstanding covenantal standard.',
                contribution: 'It gives believers and churches a clear, shared discipline, takes Old Testament economic commands seriously, and resists vague appeals to generosity that result in little practical support for ministry and the poor.',
                caution: 'The view must demonstrate that pre-Mosaic examples establish a recurring command and explain which features of Israel’s several tithes transfer to the church under the new covenant.',
                supporters: [{ name: 'Gary North', sourceId: 'perspectives-tithing' }],
                predicates: [
                    {
                        label: 'One gracious covenant preserves substantial continuity across administrations',
                        topicSlug: 'is-there-one-saving-purpose-across-the-covenants',
                        readingId: 'one-gracious-covenant',
                        linked: true,
                    },
                    {
                        label: 'The law continues to guide covenant life even where it cannot justify',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'guides-covenant-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'new-covenant-generosity', linked: true },
                    { type: 'qualifies', target: 'ten-percent-principle', linked: true },
                ],
            },
            {
                id: 'ten-percent-principle',
                title: 'Ten per cent remains a useful guide',
                thesis: 'The detailed Mosaic tithe is not directly binding, but ten per cent remains a wise starting point that trains generous and proportionate giving.',
                accent: 'amber',
                badges: ['Reformed', 'Evangelical', 'Moderate', 'Stewardship'],
                summary: 'This middle position distinguishes moral principle from covenant case law. Christians are not required to reproduce Israel’s full tithing system, yet the tenth supplies a tested benchmark rather than an arbitrary ceiling. Giving should grow beyond calculation as ability, need, gratitude, and love direct, with the local church normally receiving consistent support.',
                contribution: 'It offers practical clarity without claiming that every detail of Mosaic tithing remains law. It also frames the percentage as formation toward generosity rather than payment that discharges all further responsibility.',
                caution: 'The view must explain why ten per cent is the privileged principle if the legal structure that defines it has been fulfilled, and it can still create pressure on people in severe financial hardship.',
                supporters: [{ name: 'Reggie Kidd', sourceId: 'perspectives-tithing' }],
                predicates: [
                    {
                        label: 'The old covenant is fulfilled and transformed rather than simply discarded',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                    {
                        label: 'The law’s continuing guidance must be distinguished from its inability to justify',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'guides-covenant-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'covenantal-tithe', linked: true },
                    { type: 'overlaps with', target: 'new-covenant-generosity', linked: true },
                ],
            },
            {
                id: 'new-covenant-generosity',
                title: 'Christians should give generously without a fixed rate',
                thesis: 'The Mosaic tithe does not bind Christians; new-covenant giving is planned, proportionate, voluntary, cheerful, and sacrificial without one universal rate.',
                accent: 'teal',
                badges: ['New covenant', 'Evangelical', 'Moderate', 'Post-tithing'],
                summary: 'This reading argues that Israel’s tithes belonged to its land, priesthood, festivals, and national care for the poor. The apostles require support for ministry and generous relief but appeal to grace, equality, willingness, and capacity rather than a tenth. A wealthy Christian may be called to far more than ten per cent, while a person in acute need may first require the church’s support.',
                contribution: 'It follows the explicit language of 2 Corinthians 8–9, prevents a percentage from becoming a ceiling, and attends to differing capacity and economic vulnerability.',
                caution: 'Without concrete habits and accountable teaching, “give as led” can rationalise minimal giving. The view must also explain the continuing relevance of Abraham’s tithe, Malachi, and Jesus’ words about tithing.',
                supporters: [{ name: 'David A. Croteau', sourceId: 'perspectives-tithing' }],
                predicates: [
                    {
                        label: 'Christ inaugurates a covenantal administration not governed by the Mosaic code as a whole',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                    {
                        label: 'Works of Torah are not the basis of covenant standing in Christ',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'whole-torah',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'covenantal-tithe', linked: true },
                    { type: 'overlaps with', target: 'ten-percent-principle', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'perspectives-tithing',
                'David A. Croteau, Ken Hemphill, Bobby Eklund, Reggie Kidd, and Gary North',
                'Perspectives on Tithing: Four Views',
                'A B&H Academic comparison of the covenantal tithe, a continuing principle, and post-tithing new-covenant giving.',
                'https://www.lifeway.com/en/product/perspectives-on-tithing-P005209176',
            ),
        ],
        generation,
    },
];
